/** Design catalogue — used when searchInfluencers is unavailable (guests). */
export type DummyCreator = {
	name: string;
	handle: string;
	cat: string;
	videos: string;
	verified: boolean;
	bio: string;
	tags: string[];
	coverUrl: string;
	avatarUrl: string;
};

const MOTORSPORT_TAGS = ["Race analysis", "Onboards", "Strategy"];

const FRAMES = [
	"1742744652734-d5ec6598b5da",
	"1699138346782-8a8b211c3da2",
	"1659203206829-218b9b5930e5",
	"1614949194403-9602bdc14a3a",
	"1728116693268-125c5d6ad9e2",
	"1610905376670-5e7e0e8a3cfb",
	"1632726144626-6f6a8876e35d",
	"1673250667524-8566063b79e6",
	"1604914834278-075720506483",
	"1522519972666-d8677d9d3e67",
	"1642680096242-0f65edd6f11f",
	"1652090379496-4219a00c8ebf",
	"1744841332448-f4a2d6011202",
	"1638552708183-52e302a55e15",
];

const unsplash = (id: string, width: number) =>
	`https://images.unsplash.com/photo-${id}?fm=jpg&q=70&w=${width}&auto=format&fit=crop`;

const RAW: Array<Omit<DummyCreator, "handle" | "coverUrl" | "avatarUrl">> = [
	{
		name: "Marcus Dell",
		cat: "motorsport",
		videos: "612 videos",
		verified: true,
		bio: "Endurance racing, from Le Mans qualifying to the 4am stint nobody films.",
		tags: MOTORSPORT_TAGS,
	},
	{
		name: "Kenji Aoyama",
		cat: "motorsport",
		videos: "301 videos",
		verified: false,
		bio: "Super GT and Super Formula, with onboard laps annotated corner by corner.",
		tags: MOTORSPORT_TAGS,
	},
	{
		name: "Nora Whitby",
		cat: "cars",
		videos: "845 videos",
		verified: true,
		bio: "Restoration in real time. Six-year projects, filmed week by week.",
		tags: ["Restoration"],
	},
	{
		name: "Sal Ferreira",
		cat: "cars",
		videos: "227 videos",
		verified: false,
		bio: "Engine builds on a workshop bench, every torque figure called out loud.",
		tags: ["Engines"],
	},
	{
		name: "Hattie Grove",
		cat: "cooking",
		videos: "1,102 videos",
		verified: true,
		bio: "One pan, one hob, no props. Recipes tested until they stop failing.",
		tags: ["Weeknight"],
	},
	{
		name: "Ivo Petrov",
		cat: "cooking",
		videos: "418 videos",
		verified: false,
		bio: "Bread, mostly. Hydration percentages and oven photographs.",
		tags: ["Bread"],
	},
	{
		name: "Lena Kraus",
		cat: "music",
		videos: "533 videos",
		verified: true,
		bio: "Pedals, amps and the boring truth about what actually changes the sound.",
		tags: ["Pedals"],
	},
	{
		name: "Desmond Achebe",
		cat: "making",
		videos: "704 videos",
		verified: false,
		bio: "Furniture from rough stock, with the mistakes left in the footage.",
		tags: ["Furniture"],
	},
	{
		name: "Ruth Okonjo",
		cat: "photo",
		videos: "289 videos",
		verified: true,
		bio: "Film and digital side by side, shot on the same walk.",
		tags: ["Film"],
	},
	{
		name: "Tobias Lund",
		cat: "fitness",
		videos: "455 videos",
		verified: false,
		bio: "Marathon blocks logged in full, including the weeks that went wrong.",
		tags: ["Marathon"],
	},
	{
		name: "Aisling Byrne",
		cat: "outdoors",
		videos: "198 videos",
		verified: true,
		bio: "Long walks in bad weather, with the kit list that survived them.",
		tags: ["Hiking"],
	},
	{
		name: "Felix Moreau",
		cat: "motorsport",
		videos: "376 videos",
		verified: false,
		bio: "Rally, gravel and tarmac, with pace notes read over the onboard.",
		tags: MOTORSPORT_TAGS,
	},
	{
		name: "Wren Halliday",
		cat: "motorsport",
		videos: "244 videos",
		verified: true,
		bio: "Formula and feeder series, with radio transcripts over the onboard.",
		tags: MOTORSPORT_TAGS,
	},
	{
		name: "Isla Fenwick",
		cat: "motorsport",
		videos: "289 videos",
		verified: false,
		bio: "Touring cars from the inside of a roll cage.",
		tags: MOTORSPORT_TAGS,
	},
	{
		name: "Theo Ramsay",
		cat: "motorsport",
		videos: "533 videos",
		verified: false,
		bio: "Historic racing, engines explained before they run.",
		tags: MOTORSPORT_TAGS,
	},
	{
		name: "Amara Diallo",
		cat: "motorsport",
		videos: "348 videos",
		verified: false,
		bio: "Karting to F4, the rungs nobody films.",
		tags: MOTORSPORT_TAGS,
	},
	{
		name: "Piet van Doorn",
		cat: "motorsport",
		videos: "467 videos",
		verified: false,
		bio: "Endurance strategy, stint by stint, with the timing sheets.",
		tags: MOTORSPORT_TAGS,
	},
];

export const DUMMY_CREATORS: DummyCreator[] = RAW.map((creator, index) => ({
	...creator,
	handle: creator.name.toLowerCase().replace(/[^a-z]/g, ""),
	coverUrl: unsplash(FRAMES[index % FRAMES.length], 800),
	avatarUrl: unsplash(FRAMES[(index * 5 + 1) % FRAMES.length], 160),
}));

export const dummyCreatorsFor = (categoryId: string): DummyCreator[] =>
	DUMMY_CREATORS.filter((creator) => creator.cat === categoryId);

export const dummyCreatorByHandle = (
	handle: string,
): DummyCreator | undefined => {
	const needle = handle.replace(/^@/, "").toLowerCase();
	return DUMMY_CREATORS.find((creator) => creator.handle === needle);
};
